import { useRequestEvent, useRuntimeConfig } from '#imports'
import type { H3Event } from 'h3'

import type { Market } from '@backmarket/http-api/src/api-models/Market'
import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { Marketplace } from '@backmarket/http-api/src/api-models/Marketplace'

import type { Features } from '@backmarket/nuxt-module-marketplace/features'

import { DEFAULT_MARKET } from '../../config'
import { getRequestCountryCode } from '../../server/getRequestCountryCode'
import { createMarketplace } from '../utils/createMarketplace'
import { getMarketFromDomain } from '../utils/getMarketFromDomain'

import { useMarkets } from './useMarkets'

interface MarketplaceContext {
  market: Market
  marketplace: Marketplace
  allMarkets: Record<MarketCountryCode, Market>
  features: Features
}

/** Get marketplace context. Auto-detects from request or domain if countryCode not provided. */
export function useMarketplace(
  countryCode?: MarketCountryCode,
): MarketplaceContext {
  const event = useRequestEvent()
  const allMarkets = useMarkets()

  const config = useRuntimeConfig(event).public.marketplace
  const resolvedCountryCode = resolveCountryCode(countryCode, event)

  const market = allMarkets[resolvedCountryCode]
  const marketplace = createMarketplace(market, allMarkets)

  return {
    allMarkets,
    market,
    marketplace,
    features: config.features[resolvedCountryCode] as Features,
  }
}

function resolveCountryCode(
  countryCode?: MarketCountryCode,
  event?: H3Event,
): MarketCountryCode {
  if (countryCode) {
    return countryCode
  }

  if (event) {
    return getRequestCountryCode(event)
  }

  if (typeof window !== 'undefined') {
    return getMarketFromDomain(window.location.origin).countryCode
  }

  return DEFAULT_MARKET.countryCode
}
